/**
 * Shared canvas utilities for the image upload modals.
 *
 * Nothing here is logo- or photo-specific: every upload flow uses it. Transparency and colour
 * surgery is deliberately NOT here — it lives in ./logoPixelOps.js, which only brand-mark
 * uploads import.
 *
 * OUTPUT FORMAT RULE — read before changing any toBlob() call in this file:
 * the two functions that produce an INTERMEDIATE image (rasterizeSvg, cropImage) must stay
 * PNG. They feed the crop preview and the logo pixel-op pipeline, so switching them to a lossy
 * format would compound artefacts across steps and destroy the alpha channel mid-pipeline.
 * Only the FINAL encode is format-configurable, and that is applyTransforms — which is why it
 * is the only one here that takes a `format` argument.
 */

export const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = reject;
  img.src = src;
});

/**
 * Rasterize an SVG data URL to a PNG data URL at a reasonable resolution.
 * Intermediate — always PNG (see OUTPUT FORMAT RULE above).
 */
export const rasterizeSvg = async (svgDataUrl) => {
  const img = await loadImage(svgDataUrl);
  // Use natural dimensions, but scale up small SVGs so they have enough pixels to crop
  let w = img.naturalWidth || 512;
  let h = img.naturalHeight || 512;
  if (w < 256 || h < 256) {
    const scale = Math.max(256 / w, 256 / h);
    w = Math.round(w * scale);
    h = Math.round(h * scale);
  }
  // Cap at 2048 to avoid huge canvases
  if (w > 2048 || h > 2048) {
    const scale = Math.min(2048 / w, 2048 / h);
    w = Math.round(w * scale);
    h = Math.round(h * scale);
  }
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, w, h);
  return new Promise(resolve => {
    canvas.toBlob(blob => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    }, 'image/png');
  });
};

/**
 * Final render: applies rotation/flip, honours max width/height, and encodes.
 *
 * @param format  { type, quality?, matte? } — defaults to lossless transparent PNG, i.e. the
 *                historical behaviour. PictureUploadModal passes JPEG plus a matte colour.
 *                `matte` fills the canvas BEFORE drawing: mandatory for JPEG, because canvas
 *                toBlob composites transparent pixels onto BLACK, so a transparent source
 *                would otherwise come back with black fringing.
 */
export const applyTransforms = async (
  imageSrc,
  transforms,
  outputConstraints,
  format = { type: 'image/png' }
) => {
  const img = await loadImage(imageSrc);
  let w = img.width;
  let h = img.height;

  const rotated = transforms.rotation === 90 || transforms.rotation === 270;
  let canvasW = rotated ? h : w;
  let canvasH = rotated ? w : h;

  if (outputConstraints) {
    const { maxHeight, maxWidth } = outputConstraints;
    let scale = 1;
    if (maxHeight && canvasH > maxHeight) scale = Math.min(scale, maxHeight / canvasH);
    if (maxWidth && canvasW > maxWidth) scale = Math.min(scale, maxWidth / canvasW);
    if (scale < 1) {
      canvasW = Math.round(canvasW * scale);
      canvasH = Math.round(canvasH * scale);
      w = Math.round(w * scale);
      h = Math.round(h * scale);
    }
  }
  const canvas = document.createElement('canvas');
  canvas.width = canvasW;
  canvas.height = canvasH;
  const ctx = canvas.getContext('2d');

  // Opaque output: lay the matte down first, or transparent pixels encode to black.
  if (format.matte) {
    ctx.fillStyle = format.matte;
    ctx.fillRect(0, 0, canvasW, canvasH);
  }

  ctx.save();
  ctx.translate(canvasW / 2, canvasH / 2);
  ctx.rotate((transforms.rotation || 0) * Math.PI / 180);
  if (transforms.flipH) ctx.scale(-1, 1);
  if (transforms.flipV) ctx.scale(1, -1);
  ctx.drawImage(img, -w / 2, -h / 2, w, h);
  ctx.restore();

  return new Promise(resolve => {
    canvas.toBlob(
      blob => resolve({ blob, width: canvasW, height: canvasH }),
      format.type,
      format.quality
    );
  });
};

/**
 * Crop to the selected region, optionally letterboxed into a fixed output canvas.
 * Intermediate — always PNG (see OUTPUT FORMAT RULE above); applyTransforms does the final encode.
 */
export const cropImage = async (imageSrc, crop, displayedWidth, displayedHeight, outputSize) => {
  const img = await loadImage(imageSrc);
  const scaleX = img.naturalWidth / displayedWidth;
  const scaleY = img.naturalHeight / displayedHeight;
  const sx = crop.x * scaleX;
  const sy = crop.y * scaleY;
  const sw = crop.width * scaleX;
  const sh = crop.height * scaleY;

  const cropW = Math.round(sw);
  const cropH = Math.round(sh);

  if (outputSize) {
    // Fixed output canvas (e.g. 256x256 for icons) — scale to fit and center
    const canvas = document.createElement('canvas');
    canvas.width = outputSize.width;
    canvas.height = outputSize.height;
    const ctx = canvas.getContext('2d');
    // Scale cropped region to fit within output, preserving aspect ratio
    const scale = Math.min(outputSize.width / cropW, outputSize.height / cropH);
    const drawW = Math.round(cropW * scale);
    const drawH = Math.round(cropH * scale);
    const drawX = Math.round((outputSize.width - drawW) / 2);
    const drawY = Math.round((outputSize.height - drawH) / 2);
    ctx.drawImage(img, sx, sy, sw, sh, drawX, drawY, drawW, drawH);
    return new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
  } else {
    // No fixed output — crop to exact pixel dimensions
    const canvas = document.createElement('canvas');
    canvas.width = cropW;
    canvas.height = cropH;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, cropW, cropH);
    return new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
  }
};

export const formatSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

// Build output spec string for the upload drop zone
export const getOutputSpec = (aspectRatio, outputSize, maxOutputHeight, maxOutputWidth) => {
  if (outputSize) return `${outputSize.width} x ${outputSize.height}px`;
  const parts = [];
  if (maxOutputWidth) parts.push(`${maxOutputWidth}px wide`);
  if (maxOutputHeight) parts.push(`${maxOutputHeight}px tall`);
  if (aspectRatio === 1) parts.push('square');
  return parts.length ? 'max ' + parts.join(', ') : null;
};
